export type InvoiceLineClassification =
  | "menu_item"
  | "service_commitment"
  | "station_setup"
  | "equipment_resource"
  | "rental_third_party"
  | "commercial_only";

export const invoiceLineClassificationLabels: Record<InvoiceLineClassification, string> = {
  menu_item: "Menu Item",
  service_commitment: "Service Commitment",
  station_setup: "Station / Service Setup",
  equipment_resource: "Equipment / Resource",
  rental_third_party: "Rental / Third Party",
  commercial_only: "Commercial Only",
};

export type PaymentScheduleEntry = {
  amount?: number;
  dueDate?: string;
  paymentDate?: string;
  paymentNumber?: string;
  status?: string;
  rawLine: string;
};

export type ParsedInvoiceLine = {
  id: string;
  rawLine: string;
  name: string;
  description?: string;
  rawQuantity: number;
  rawUnitLabel?: string;
  interpretedUnit?: string;
  /** Management-reviewed quantity when detected qty is ambiguous. */
  interpretedQuantity?: number;
  unitPrice: number;
  lineTotal: number;
  classification: InvoiceLineClassification;
  needsReview: boolean;
  rawNotes?: string;
};

export type ParsedInvoiceDraft = {
  sourceFilename: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  invoiceNumber?: string;
  invoiceIssueDate?: string;
  projectName?: string;
  eventType?: string;
  eventDate?: string;
  venue?: string;
  lines: ParsedInvoiceLine[];
  subtotal?: number;
  invoiceTotal?: number;
  paymentSchedule: PaymentScheduleEntry[];
  parseWarnings: string[];
};

export type InvoiceExampleCatalogEntry = {
  filename: string;
  url: string;
};

export type InvoiceImportSourceKind = "catalog" | "upload";

/** Where a PDF came from — catalog sample or browser upload. */
export type InvoiceImportSource = {
  kind: InvoiceImportSourceKind;
  filename: string;
  /** Catalog PDF URL served by Vite. */
  url?: string;
};

export type ImportParseStatus = "waiting" | "parsing" | "parsed" | "failed";

/** Combined queue display status (parse + review + migration complete). */
export type ImportDisplayStatus =
  | "waiting"
  | "parsing"
  | "parsed"
  | "needs_review"
  | "ready"
  | "failed"
  | "imported";

export const importDisplayStatusLabels: Record<ImportDisplayStatus, string> = {
  waiting: "Waiting",
  parsing: "Parsing",
  parsed: "Parsed",
  needs_review: "Needs Review",
  ready: "Ready",
  failed: "Failed",
  imported: "Imported",
};

/** Normalized import candidate produced by the parser and held in the batch queue. */
export type InvoiceImportCandidate = {
  id: string;
  source: InvoiceImportSource;
  parseStatus: ImportParseStatus;
  draft?: ParsedInvoiceDraft;
  error?: string;
  /** Management confirmed review on the review screen. */
  markedReady: boolean;
  /** Set when this intake item has been converted into an Event. */
  importedEventId?: string;
  importedAt?: string;
  /** True when a source PDF is stored on the backend. */
  hasSourceDocument?: boolean;
};

export function catalogImportId(filename: string): string {
  return `catalog:${filename}`;
}

export function uploadImportId(token: string): string {
  return `upload:${token}`;
}
